import cx from 'classnames';
import React, {
  MouseEventHandler,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Badge } from 'reactstrap';
import ListViewItem from '../ListViewItem';
import Skeleton from '../Skeleton';
import { accountSelector } from '~redux/account/selectors/account';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import { Brand } from '~redux/brand/types';
import { makeFavoriteBrandSelector } from '~redux/favorite/selectors';
import { addFavoriteBrand, removeFavoriteBrand } from '~redux/favorite/thunk';
import { isProtectedBrand } from '~utils/brand';
import { formatCurrency } from '~utils/formatter';
import './styles.scss';
import { useThunk } from '~utils/hooks';
import { getPublicImageUrl } from '~utils/image';

const Item: React.FC<{ brand: Brand; image?: string }> = ({ brand, image }) => {
  const account = useSelector(accountSelector);
  const user = useSelector(authUserSelector);
  const url = useMemo(() => `/brand/${brand.id}`, [brand.id]);
  const minimum = useMemo(
    () =>
      brand.brand_minimum_buy == 0
        ? `最小発注金額：設定なし`
        : `最小発注金額：${formatCurrency(brand.brand_minimum_buy ?? 0)}`,
    [brand.brand_minimum_buy]
  );

  const [addToFavorite, adding] = useThunk(addFavoriteBrand);
  const [removeFromFavorite, removing] = useThunk(removeFavoriteBrand);
  const favorite = useSelector(makeFavoriteBrandSelector(brand.id));
  const onFollowLinkClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!user) {
        return;
      }
      if (!favorite) {
        addToFavorite(user, brand.id);
        return;
      }
      removeFromFavorite(favorite.id, user);
    },
    [user, favorite]
  );

  const isProtected = useMemo(
    () => account && isProtectedBrand(brand, account),
    [brand, account]
  );

  const [isHover, setIsHover] = useState(false);

  const isNewBrand = useMemo(() => {
    let isNewBrand = false;
    brand.product?.items?.forEach((product) => {
      const dt = new Date();
      const newBrandDueDt = new Date();
      newBrandDueDt.setDate(newBrandDueDt.getDate() - 30);
      const itemAddDate = new Date(product.createdAt);
      if (newBrandDueDt <= itemAddDate && itemAddDate <= dt) {
        isNewBrand = true;
        return;
      }
    });
    return isNewBrand;
  }, [brand]);

  return (
    <ListViewItem className="brand-list-view-item">
      <Link
        to={url}
        className="brand-list-view-item-link"
        onPointerEnter={() => setIsHover(true)}
        onPointerLeave={() => setIsHover(false)}
      >
        <div className="brand-list-view-item-image-wrapper">
          <div className="brand-list-view-item-image">
            {isNewBrand && (
              <h4 className="brand-new-mark">
                <Badge pill color="info">
                  新着あり
                </Badge>
              </h4>
            )}
            <img
              src={
                image ??
                getPublicImageUrl(brand.brand_icon_imageKey || '', {
                  w: 400,
                  h: 400,
                })
              }
              alt={brand.brand_name}
              className="thumbnail-brand border"
              loading="lazy"
            />
            {user && (
              <span
                className="brand-list-view-item-follow-link"
                onClick={onFollowLinkClick}
                //@ts-ignore
                disabled={adding || removing}
              >
                <i
                  className={cx({
                    'brand-list-view-item-hart-icon': true,
                    followed: !!favorite,
                  })}
                />
              </span>
            )}
            {isHover && (
              <p className="brand-description">{brand.brand_content}</p>
            )}
          </div>
        </div>
      </Link>
      <div className="information py-2">
        <div className="title text-center">
          <Link to={url}>
            <strong translate="no">{brand.brand_name}</strong>
          </Link>
        </div>
        {user && !isProtected && (
          <div className="text-center">
            <span>{minimum}</span>
          </div>
        )}
      </div>
      <div className="description">{brand.brand_content}</div>
    </ListViewItem>
  );
};

const SkeletonItem = () => (
  <ListViewItem className="brand-list-view-item">
    <div className="brand-list-view-item-image" />
    <div className="information py-2 d-flex justify-content-center">
      <Skeleton width={180} />
    </div>
  </ListViewItem>
);

type BrandListViewItemProps = {
  brand?: Brand;
  image?: string;
};

const BrandListViewItem: React.FC<BrandListViewItemProps> = ({
  brand,
  image,
}) => {
  if (!brand) {
    return <SkeletonItem />;
  }
  return <Item brand={brand} image={image} />;
};

export default BrandListViewItem;
